"use client";

import { useState, type ComponentProps, type ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Icon } from "@/components/ui/icon";

type ContainerProps = ComponentProps<"div"> & {
  variant?: "thread" | "reply";
};

function Container({
  variant = "thread",
  className,
  ...props
}: ContainerProps) {
  return (
    <div
      className={cn(
        "flex w-full flex-col gap-3",
        variant === "thread" &&
          "overflow-hidden rounded-lg border border-border bg-contrast/3 p-5 opacity-[0.96] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
        variant === "reply" &&
          "rounded-lg border border-border bg-contrast/[0.02] p-3",
        className,
      )}
      {...props}
    />
  );
}

function Header({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn("flex w-full items-center justify-between", className)}
      {...props}
    />
  );
}

function Timestamp({ className, ...props }: ComponentProps<"span">) {
  return (
    <span className={cn("text-xs text-foreground/50", className)} {...props} />
  );
}

function Content({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "w-full whitespace-pre-wrap break-words text-[13px] font-normal leading-normal text-foreground/70",
        className,
      )}
      {...props}
    />
  );
}

type ReplyButtonProps = {
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

function ReplyButton({ onClick, disabled, className }: ReplyButtonProps) {
  const [animKey, setAnimKey] = useState(0);

  return (
    <motion.button
      type="button"
      disabled={disabled}
      onClick={() => {
        setAnimKey((k) => k + 1);
        onClick();
      }}
      className={cn(
        "flex items-center gap-2 text-xs text-foreground/70 transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", duration: 0.15, bounce: 0 }}
    >
      <div className="opacity-[0.65]">
        <motion.div
          key={animKey}
          initial={{ scale: 0.25, opacity: 0, filter: "blur(4px)" }}
          animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
          transition={{ type: "spring", duration: 0.3, bounce: 0 }}
        >
          <Icon
            name="IconArrowShareRight"
            size={16}
            className="text-foreground"
          />
        </motion.div>
      </div>
      Reply
    </motion.button>
  );
}

function Separator({ className, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("h-px w-full bg-contrast/10", className)} {...props} />
  );
}

type RepliesHeadingProps = {
  count: number;
  className?: string;
};

function RepliesHeading({ count, className }: RepliesHeadingProps) {
  return (
    <p className={cn("text-xs font-medium leading-none", className)}>
      <span className="text-foreground">Replies</span>
      <span className="text-foreground/70">{` (${count})`}</span>
    </p>
  );
}

type SeeAllRepliesButtonProps = {
  onClick: () => void;
  className?: string;
};

function SeeAllRepliesButton({ onClick, className }: SeeAllRepliesButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex h-[37px] w-full items-center justify-center gap-1.5 overflow-hidden rounded-full border border-border bg-surface-elevated px-3.5 py-2.5 transition-colors hover:bg-contrast/8",
        className,
      )}
    >
      <span className="text-sm font-medium text-foreground/70">
        See all replies
      </span>
      <Icon
        name="IconChevronDownSmall"
        size={16}
        className="text-foreground opacity-[0.65]"
      />
    </button>
  );
}

/** Row: reply on the left, trailing actions (reactions + menu) on the right */
function ActionsRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex w-full items-center justify-between", className)}>
      {children}
    </div>
  );
}

export const Note = {
  Container,
  Header,
  Timestamp,
  Content,
  ReplyButton,
  Separator,
  RepliesHeading,
  SeeAllRepliesButton,
  ActionsRow,
};
